import { useEffect, useState } from 'react';
import groupLight from '../../assets/images/bebrave-group.svg';
import groupDark from '../../assets/images/bebrave-group-dark.svg';
import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import databaseIcon from 'assets/images/database.svg';
import cardGradientEllipse from 'assets/images/card-gradient-ellipse.svg';

import { UProfileMenu } from 'components/blocks/u-profile-menu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignatureResponse } from 'pages/hooks/use-home';
import { getAvailableApps, handleConfigItemClick } from 'pages/home/models/homeConfigs';
import { useTranslation } from 'react-i18next';
// import { HomeDynamicHeader } from './components/HomeDynamicHeader';
import { useTheme } from 'components/core/theme-provider';
import { useCommonStore } from "state/store/common/useCommonStore";
import { useWhiteLabelStore } from 'state/store/white-label/useWhiteLabelStore';
import homeBannerImage from 'assets/images/home-banner.svg';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';
import { SettingsDialog } from 'components/blocks/settings-dialog';
import { useSubscriptionStore } from 'state/store/subscription';
import { useIsMobile } from 'hooks/use-mobile';
import { PLANS } from 'constant/plan';
import ftpIcon from 'assets/images/ftp.svg';

const urlParamToTabId: Record<string, string> = {
  'internal-apps': 'internalApps',
  'external-apps': 'externalApps'
};

const tabIdToUrlParam: Record<string, string> = {
  internalApps: 'internal-apps',
  externalApps: 'external-apps'
};

// Icon Components for Connectors
const GoogleDriveIcon = () => (
  <img src={gdriveIcon} alt="Google Drive" width="40" height="40" className="w-full h-full" />
);

const OneDriveIcon = () => (
  <img src={onedriveIcon} alt="Microsoft OneDrive" width="40" height="40" className="w-full h-full" />
);

const DatabaseIcon = () => (
  <img src={databaseIcon} alt="Database" width="40" height="40" className="w-full h-full" />
);

const SFTPIcon = () => (
  <img src={ftpIcon} alt="SFTP" width="40" height="40" className="w-full h-full" />
);

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync } = useSignatureResponse();
  const { theme } = useTheme();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const { settings: whiteLabelSettings } = useWhiteLabelStore();
  const { isConnected } = useIntegrationsStore();
  const currentPlan = useSubscriptionStore((state) => state.currentPlan);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tabId, setTabId] = useState('internalApps');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const shouldHideConnectors = !(currentPlan && (currentPlan.name !== PLANS.NOPLAN && currentPlan.name !== PLANS.FREE && currentPlan.name !== PLANS.BASIC));
  const logoSrc =
    theme === 'dark'
      ? (whiteLabelSettings?.logoUrlDarkMode || groupDark)
      : (whiteLabelSettings?.logoUrl || groupLight);

  const connectors = [
    {
      id: 'google-drive',
      name: 'Google Drive',
      description: 'ACCESS_AND_SEARCH_YOUR_GOOGLE_DRIVE_FILES',
      icon: <GoogleDriveIcon />,
      isAvailable: false
    },
    {
      id: 'onedrive',
      name: 'Microsoft OneDrive',
      description: 'ACCESS_AND_SEARCH_YOUR_ONEDRIVE_FILES',
      icon: <OneDriveIcon />,
      isAvailable: true
    },
    {
      id: 'database',
      name: 'Database',
      description: 'CONNECT_TO_YOUR_DATABASES',
      icon: <DatabaseIcon />,
      isAvailable: true
    },
    {
      id: 'sftp',
      name: 'SFTP',
      description: 'CONNECT_TO_SFTP_SERVER',
      icon: <SFTPIcon />,
      isAvailable: true
    },
  ];

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && urlParamToTabId[tabParam]) {
      setTabId(urlParamToTabId[tabParam]);
    }
  }, [searchParams]);

  const handleTabChange = (newTabId: string) => {
    setTabId(newTabId);
    const urlParam = tabIdToUrlParam[newTabId];
    if (urlParam) {
      setSearchParams({ tab: urlParam });
    }
  };

  const handleConnectorClick = (connectorId: string) => {
    // Store which connector was clicked
    setSelectedConnectorId(connectorId);
    // Open settings dialog
    setIsSettingsOpen(true);
  };

  const handleSettingsClose = (isOpen: boolean) => {
    setIsSettingsOpen(isOpen);
    if (!isOpen) {
      // Reset selected connector when dialog closes
      setSelectedConnectorId(null);
    }
  };

  return (
    <div className="h-screen flex flex-col justify-between w-full">
      {/* Header */}
      <header className={`w-full flex px-6 py-2 items-center justify-between`}>
        <div className="w-32 h-8 inline-flex justify-start items-center gap-1.5">
          <img
            src={logoSrc}
            className="h-10 object-contain"
            alt="eaglegpt logo"
          />
        </div>

        <div className="flex justify-between items-center gap-1 sm:gap-3 md:gap-4">
          {/* <HomeDynamicHeader />      */}
          {/* <div className="border-l border-gray-300 h-4"></div> */}
          <UProfileMenu isDropdownIconRequired={true} isFullNameRequired={true} />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col max-w-[912px] w-full p-[2rem] mx-auto mb-auto">
        <div className="flex flex-col gap-8 w-full">
          {/* Banner */}
          <div
            className="h-[122px] min-w-full flex items-center justify-start rounded-[8px] bg-cover bg-center"
            style={{ backgroundImage: `url(${homeBannerImage})` }}
          >
            <div className={`flex flex-col gap-1 ${theme === 'dark' ? 'text-neutral-700' : 'text-neutral-100'} font-bold pl-7`}>
              <span className="text-lg">{t('AVAILABLE')}</span>
              <span className="text-[32px]">{t('APPS')}</span>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={tabId} className="w-full">
            <div className="flex items-center rounded text-base">
              <TabsList className="border border-primary p-1 rounded-[6px] font-medium">
                <TabsTrigger
                  value="internalApps"
                  onClick={() => handleTabChange('internalApps')}
                  className={`data-[state=active]:bg-primary-600 ${theme === 'dark' ? 'data-[state=active]:text-neutral-800 text-neutral-50' : 'data-[state=active]:text-neutral-50 text-neutral-800'} rounded-[4px] px-[12px] py-[6px]`}
                >
                  {t('INTERNAL_APPS')}
                </TabsTrigger>

                <TabsTrigger
                  value="externalApps"
                  onClick={() => handleTabChange('externalApps')}
                  className={`data-[state=active]:bg-primary-600 ${theme === 'dark' ? 'data-[state=active]:text-neutral-800 text-neutral-50' : 'data-[state=active]:text-neutral-50 text-neutral-800'} rounded-[4px] px-[12px] py-[6px]`}
                >
                  {t('EXTERNAL_APPS')}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Internal Apps */}
            <TabsContent value="internalApps" className="w-full">
              <div className="flex flex-wrap gap-4 mt-8">
                {getAvailableApps(isAdmin, isOrgAdmin)?.map((item, index) => (
                  <div
                    onClick={() => handleConfigItemClick(item, navigate, mutateAsync)}
                    key={index}
                    className={`${isMobile ? 'w-full' : 'w-[272px]'} relative flex flex-col items-center gap-4 px-6 pt-8 pb-12 group transition rounded-lg dark:shadow-[0px_8px_12px_0px_rgba(22,22,25,0.16)] shadow-all-sides-lg cursor-pointer overflow-hidden ${theme === 'dark' ? 'z-[11] [&:not(:hover)]:bg-black-100' : 'bg-white border-[0.5px] border-gray-200 hover:bg-primary-600'}`}
                    style={theme === 'dark' ? {
                      background: 'linear-gradient(#0A0A0B, #0A0A0B) padding-box, linear-gradient(135deg, transparent 0%, transparent 20%, rgba(172, 149, 120, 0.5) 30%, rgba(172, 149, 120, 0) 70%, transparent 80%, transparent 100%) border-box, linear-gradient(315deg, transparent 0%, transparent 20%, rgba(172, 149, 120, 0.5) 30%, rgba(172, 149, 120, 0) 70%, transparent 80%, transparent 100%) border-box',
                      border: '0.5px solid transparent'
                    } : undefined}
                  >
                    {/* Ellipse gradient overlay for dark mode only - only show for EagleGPT (id === '1') */}
                    {item.id === '1' && theme === 'dark' && (
                      <div className="absolute h-[26px] left-[20.5px] bottom-[-16px] w-[231px] pointer-events-none">
                        <div className="absolute inset-[-169.23%_-19.05%_-219.23%_-19.05%]">
                          <img alt="" className="block max-w-none size-full" src={cardGradientEllipse} />
                        </div>
                      </div>
                    )}

                    <div className="h-[44px] w-[44px] rounded-2xl relative z-10">
                      <div className="h-10 w-10">
                        {item.icon}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 items-center w-full relative z-10">
                      <div className={`text-center ${theme === 'dark' ? 'text-primary-200 group-hover:text-neutral-800' : 'text-primary-600 group-hover:text-white'} transition duration-200 text-[16px] font-semibold leading-[1.2] whitespace-nowrap overflow-hidden text-ellipsis w-full`}>
                        {t(item.title)}
                      </div>
                      <div className={`font-light text-center ${theme === 'dark' ? 'text-neutral-300 group-hover:text-neutral-800' : 'text-neutral-800 group-hover:text-white'} transition duration-200 text-[12px] leading-[1.2] tracking-[0.3px] overflow-hidden line-clamp-3 w-full`}>
                        {t(item.description)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* External Apps - Connector Cards */}
            <TabsContent value="externalApps" className="w-full">
              {shouldHideConnectors ?
                <div className="mt-[30vh] text-center text-primary text-[16px] font-semibold leading-[1.2] whitespace-nowrap overflow-hidden text-ellipsis w-full">
                  {t('EXTERNAL_APPS_UNAVAILABLE_FOR_FREE_PLAN')}
                </div> :
                <div className="flex flex-wrap gap-4 mt-8">
                  {connectors.filter(connector => connector.isAvailable).map((connector) => {
                    const connectorIsConnected = isConnected(connector.id);

                    return (
                      <div
                        key={connector.id}
                        onClick={() => handleConnectorClick(connector.id)}
                        className={`${isMobile ? 'w-full' : 'w-[272px]'} relative flex flex-col items-center gap-4 px-6 py-6 group transition rounded-lg dark:shadow-[0px_8px_12px_0px_rgba(22,22,25,0.16)] shadow-all-sides-lg cursor-pointer ${theme === 'dark' ? 'bg-black-100 z-[11]' : 'bg-white border-[0.5px] border-gray-200 hover:bg-primary-600'}`}
                        style={theme === 'dark' ? {
                          background: 'linear-gradient(#0A0A0B, #0A0A0B) padding-box, linear-gradient(135deg, transparent 0%, transparent 20%, rgba(172, 149, 120, 0.5) 30%, rgba(172, 149, 120, 0) 70%, transparent 80%, transparent 100%) border-box, linear-gradient(315deg, transparent 0%, transparent 20%, rgba(172, 149, 120, 0.5) 30%, rgba(172, 149, 120, 0) 70%, transparent 80%, transparent 100%) border-box',
                          border: '0.5px solid transparent'
                        } : undefined}
                      >
                        <div className="h-[44px] w-[44px] rounded-2xl relative z-10">
                          <div className="h-10 w-10">
                            {connector.icon}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 items-center w-full relative z-10">
                          <div className={`text-center ${theme === 'dark' ? 'text-primary-200 group-hover:text-neutral-800' : 'text-primary-600 group-hover:text-white'} transition duration-200 text-[16px] font-semibold leading-[1.2] whitespace-nowrap overflow-hidden text-ellipsis w-full`}>
                            {connector.name}
                          </div>
                          <div className={`font-light text-center ${theme === 'dark' ? 'text-neutral-300 group-hover:text-neutral-800' : 'text-neutral-800 group-hover:text-white'} transition duration-200 text-[12px] leading-[1.2] tracking-[0.3px] overflow-hidden line-clamp-3 w-full`}>
                            {t(connector.description)}
                          </div>
                          {connectorIsConnected && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className={`text-xs ${theme === 'dark' ? 'text-neutral-300 group-hover:text-neutral-800' : 'text-neutral-800 group-hover:text-white'} transition duration-200`}>
                                {t('CONNECTED')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <SettingsDialog
        isOpen={isSettingsOpen}
        setIsOpen={handleSettingsClose}
        currentPlan={currentPlan || null}
        initialTab="connectors"
        selectedConnectorId={selectedConnectorId}
        hideNavigation={true}
      />
      <div />
    </div>
  );
}